import { styled } from '@mui/material';
import { Icon } from '../../atoms/Icon/Icon';

// Sprite sizes that draw each glyph at the design's 32px; the sprites carry different built-in padding.
const EMPTY_STATE_ICONS = {
    noGoals: { id: 'target_rounded_md', size: 37 },
    noFunding: { id: 'no-funding-source', size: 35 }
} as const;

export type TermEmptyStateVariant = keyof typeof EMPTY_STATE_ICONS;

// Text metrics from the design; the theme has no matching typography variants.
const TITLE_METRICS = { fontSize: '18px', lineHeight: '25px' };
const SUBTITLE_METRICS = { fontSize: '16px', lineHeight: '22px' };

const Root = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
    paddingBlock: theme.spacing(3)
}));

const IconCircle = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: '50%',
    backgroundColor: theme.palette.semantic.bg.brandLight1,
    '& > svg': { color: theme.palette.semantic.icon.brand }
}));

const TextGroup = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    textAlign: 'center'
}));

const Title = styled('p')(({ theme }) => ({
    margin: 0,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    ...TITLE_METRICS,
    color: theme.palette.semantic.text.default
}));

const Subtitle = styled('p')(({ theme }) => ({
    margin: 0,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    ...SUBTITLE_METRICS,
    color: theme.palette.semantic.text.muted
}));

type TermEmptyStateProps = {
    variant: TermEmptyStateVariant;
    title: string;
    subtitle: string;
};

/** Empty state for a Funding Navigator term: an icon in a circle, a title and a muted subtitle. */
export const TermEmptyState = ({ variant, title, subtitle }: TermEmptyStateProps) => {
    const icon = EMPTY_STATE_ICONS[variant];
    return (
        <Root>
            <IconCircle>
                <Icon id={icon.id} size={icon.size} />
            </IconCircle>
            <TextGroup>
                <Title>{title}</Title>
                <Subtitle>{subtitle}</Subtitle>
            </TextGroup>
        </Root>
    );
};
